import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import {
  ArrowLeft,
  Loader2,
  Coins,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Sparkles,
  ImageIcon,
  Video,
  PenLine,
  Search,
  CalendarDays,
  MessageCircle,
  RefreshCw,
  Users,
  AlertTriangle,
  Gift,
  ShoppingCart,
  Percent,
} from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

// Estimated API cost per credit (what it costs YOU per credit consumed)
const COST_PER_CREDIT = 0.01;

const TOOL_LABELS: Record<string, { label: string; icon: React.ReactNode; costPerCredit: number }> = {
  'image-generator': { label: 'Image Creator', icon: <ImageIcon className="w-4 h-4" />, costPerCredit: 0.01 },
  'video-script': { label: 'Video Script', icon: <Video className="w-4 h-4" />, costPerCredit: 0.0015 },
  'caption-hook': { label: 'Captions & Hooks', icon: <PenLine className="w-4 h-4" />, costPerCredit: 0.003 },
  'niche-analyzer': { label: 'Niche Analyzer', icon: <Search className="w-4 h-4" />, costPerCredit: 0.001 },
  'content-planner': { label: 'Content Planner', icon: <CalendarDays className="w-4 h-4" />, costPerCredit: 0.001 },
  'study-assistant': { label: 'Chat Teacher', icon: <MessageCircle className="w-4 h-4" />, costPerCredit: 0.002 },
};

// Credit pack pricing for revenue mapping
const CREDIT_PACK_PRICES: Record<number, { credits: number; name: string }> = {
  299: { credits: 50, name: 'Starter' },
  999: { credits: 200, name: 'Growth' },
  2299: { credits: 500, name: 'Pro' },
  6999: { credits: 2000, name: 'Elite' },
  49900: { credits: 35000, name: 'Ultimate' },
};

interface UsageByTool {
  tool_type: string;
  total_credits: number;
  total_uses: number;
}

interface TopUser {
  client_id: string;
  client_name: string;
  client_email: string;
  client_plan: string;
  total_credits: number;
  total_uses: number;
  plan_allowance: number;
}

const AdminAITools = () => {
  const navigate = useNavigate();
  const { user, role, loading: authLoading } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Platform stats
  const [totalCreditsUsed, setTotalCreditsUsed] = useState(0);
  const [usageByTool, setUsageByTool] = useState<UsageByTool[]>([]);
  const [topUsers, setTopUsers] = useState<TopUser[]>([]);
  const [totalActiveCredits, setTotalActiveCredits] = useState(0);
  const [totalClients, setTotalClients] = useState(0);

  // Real Stripe credit pack data
  const [creditPackRevenue, setCreditPackRevenue] = useState(0);
  const [creditPackCount, setCreditPackCount] = useState(0);
  const [stripeLoading, setStripeLoading] = useState(false);

  // Purchased credits from usage log (credit_purchase deposits)
  const [totalCreditsPurchased, setTotalCreditsPurchased] = useState(0);

  useEffect(() => {
    if (!authLoading) {
      if (!user) navigate('/login');
      else if (role !== 'admin') {
        toast.error('Access denied');
        navigate('/dashboard');
      }
    }
  }, [user, role, authLoading, navigate]);

  const loadStripeData = useCallback(async () => {
    setStripeLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('get-stripe-revenue', {
        body: { period: 'year' },
      });
      if (error) throw error;
      if (data) {
        setCreditPackRevenue(data.creditPackRevenue || 0);
        setCreditPackCount(data.creditPackCount || 0);
      }
    } catch (e) {
      console.error('Stripe credit data error:', e);
    }
    setStripeLoading(false);
  }, []);

  const loadData = useCallback(async () => {
    try {
      // Fetch all usage logs
      const { data: usageLogs, error: usageError } = await supabase
        .from('ai_usage_log')
        .select('client_id, tool_type, credits_used, created_at');

      if (usageError) throw usageError;

      // Fetch all credits records
      const { data: creditsData, error: creditsError } = await supabase
        .from('ai_credits')
        .select('client_id, balance, monthly_allowance, total_used');

      if (creditsError) throw creditsError;

      // Fetch clients for name/plan mapping
      const { data: clientsData, error: clientsError } = await supabase
        .from('clients')
        .select('id, name, email, plan, paid');

      if (clientsError) throw clientsError;

      const clientMap = new Map(clientsData?.map(c => [c.id, c]) || []);

      // Separate actual usage from credit purchase deposits
      const actualUsage = (usageLogs || []).filter(l => !l.tool_type.startsWith('credit_purchase'));
      const purchaseDeposits = (usageLogs || []).filter(l => l.tool_type.startsWith('credit_purchase'));

      // Total credits consumed by users
      const totalUsed = actualUsage.reduce((sum, log) => sum + Math.max(0, log.credits_used), 0);
      setTotalCreditsUsed(totalUsed);

      // Total credits purchased (deposited via packs)
      const purchased = purchaseDeposits.reduce((sum, log) => sum + Math.abs(log.credits_used), 0);
      setTotalCreditsPurchased(purchased);

      // Usage by tool (exclude purchases)
      const toolMap = new Map<string, { credits: number; uses: number }>();
      actualUsage.forEach(log => {
        const existing = toolMap.get(log.tool_type) || { credits: 0, uses: 0 };
        existing.credits += Math.max(0, log.credits_used);
        existing.uses += 1;
        toolMap.set(log.tool_type, existing);
      });

      const byTool: UsageByTool[] = Array.from(toolMap.entries())
        .map(([tool, data]) => ({ tool_type: tool, total_credits: data.credits, total_uses: data.uses }))
        .sort((a, b) => b.total_credits - a.total_credits);
      setUsageByTool(byTool);

      // Active credit balances
      const activeBalance = creditsData?.reduce((sum, c) => sum + c.balance, 0) || 0;
      setTotalActiveCredits(activeBalance);
      setTotalClients(clientsData?.length || 0);

      // Top users by credit consumption (exclude purchases)
      const userUsageMap = new Map<string, { credits: number; uses: number }>();
      actualUsage.forEach(log => {
        const existing = userUsageMap.get(log.client_id) || { credits: 0, uses: 0 };
        existing.credits += Math.max(0, log.credits_used);
        existing.uses += 1;
        userUsageMap.set(log.client_id, existing);
      });

      const topUsersList: TopUser[] = Array.from(userUsageMap.entries())
        .map(([clientId, data]) => {
          const client = clientMap.get(clientId);
          const plan = client?.plan?.toLowerCase() || 'freemium';
          const allowance = plan === 'lifetime' ? 500 : plan === 'monthly' ? 100 : 10;
          return {
            client_id: clientId,
            client_name: client?.name || 'Unknown',
            client_email: client?.email || '',
            client_plan: client?.plan || 'Freemium',
            total_credits: data.credits,
            total_uses: data.uses,
            plan_allowance: allowance,
          };
        })
        .sort((a, b) => b.total_credits - a.total_credits)
        .slice(0, 15);
      setTopUsers(topUsersList);
    } catch (error) {
      console.error('Error loading AI data:', error);
      toast.error('Failed to load AI analytics');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    if (user && role === 'admin') {
      loadData();
      loadStripeData();
    }
  }, [user, role, loadData, loadStripeData]);

  const handleRefresh = () => {
    setRefreshing(true);
    loadData();
    loadStripeData();
  };

  const giveCredits = async (clientId: string, clientName: string) => {
    try {
      const { data: current } = await supabase
        .from('ai_credits')
        .select('balance')
        .eq('client_id', clientId)
        .single();

      if (!current) {
        await supabase.from('ai_credits').insert({ client_id: clientId, balance: 10 });
      } else {
        await supabase
          .from('ai_credits')
          .update({ balance: current.balance + 10, updated_at: new Date().toISOString() })
          .eq('client_id', clientId);
      }

      toast.success(`Gave 10 free credits to ${clientName}`);
      loadData();
    } catch (err) {
      console.error(err);
      toast.error('Failed to give credits');
    }
  };

  // Calculate real platform cost from usage
  const estimatedPlatformCost = usageByTool.reduce((sum, tool) => {
    const costInfo = TOOL_LABELS[tool.tool_type];
    const perCreditCost = costInfo?.costPerCredit || COST_PER_CREDIT;
    return sum + (tool.total_credits * perCreditCost);
  }, 0);

  // Use REAL Stripe credit pack revenue
  const realRevenue = creditPackRevenue;
  const profitMargin = realRevenue > 0
    ? ((realRevenue - estimatedPlatformCost) / realRevenue) * 100
    : 0;
  const netProfit = realRevenue - estimatedPlatformCost;

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border bg-white/80 backdrop-blur-sm"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <span className="text-lg font-semibold text-foreground">
            Faceless Aura <span className="text-primary">Club</span>
          </span>
          <div className="h-6 w-px bg-border" />
          <span className="text-sm font-medium text-primary flex items-center gap-1.5">
            <Sparkles className="w-4 h-4" />
            AI Tools Management
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={handleRefresh} disabled={refreshing}>
          <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </motion.header>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Title */}
        <motion.div className="mb-6" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground">AI Tools Analytics</h1>
          <p className="text-sm text-muted-foreground mt-1">Real credit pack revenue from Stripe · Usage & cost tracking</p>
        </motion.div>

        {/* Top Stats Cards - 6 cards */}
        <motion.div
          className="grid grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          {/* Credit Pack Revenue (REAL from Stripe) */}
          <div className="p-4 sm:p-5 bg-white border border-border rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center">
                <DollarSign className="w-4 h-4 text-green-600" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Credit Pack Revenue</span>
            </div>
            {stripeLoading ? (
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            ) : (
              <>
                <p className="text-2xl sm:text-3xl font-bold text-green-600">${realRevenue.toFixed(2)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {creditPackCount} pack{creditPackCount !== 1 ? 's' : ''} sold (Stripe, 1Y)
                </p>
              </>
            )}
          </div>

          {/* API Cost */}
          <div className="p-4 sm:p-5 bg-white border border-border rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center">
                <TrendingDown className="w-4 h-4 text-red-600" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Est. API Cost</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-red-600">${estimatedPlatformCost.toFixed(2)}</p>
            <p className="text-xs text-muted-foreground mt-1">Across all tools</p>
          </div>

          {/* Net Profit */}
          <div className="p-4 sm:p-5 bg-white border border-border rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-9 h-9 rounded-xl ${netProfit >= 0 ? 'bg-green-100' : 'bg-red-100'} flex items-center justify-center`}>
                <TrendingUp className={`w-4 h-4 ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`} />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Net Profit</span>
            </div>
            <p className={`text-2xl sm:text-3xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
              ${netProfit.toFixed(2)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Revenue − API cost</p>
          </div>

          {/* Total Credits Used */}
          <div className="p-4 sm:p-5 bg-white border border-border rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                <Coins className="w-4 h-4 text-amber-600" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Credits Used</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-foreground">{totalCreditsUsed.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Total consumed</p>
          </div>

          {/* Credits Purchased */}
          <div className="p-4 sm:p-5 bg-white border border-border rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-blue-100 flex items-center justify-center">
                <ShoppingCart className="w-4 h-4 text-blue-600" />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Credits Purchased</span>
            </div>
            <p className="text-2xl sm:text-3xl font-bold text-blue-600">{totalCreditsPurchased.toLocaleString()}</p>
            <p className="text-xs text-muted-foreground mt-1">Via credit packs</p>
          </div>

          {/* Profit Margin */}
          <div className="p-4 sm:p-5 bg-white border border-border rounded-2xl shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <div className={`w-9 h-9 rounded-xl ${profitMargin >= 50 ? 'bg-green-100' : profitMargin >= 0 ? 'bg-amber-100' : 'bg-red-100'} flex items-center justify-center`}>
                <Percent className={`w-4 h-4 ${profitMargin >= 50 ? 'text-green-600' : profitMargin >= 0 ? 'text-amber-600' : 'text-red-600'}`} />
              </div>
              <span className="text-xs font-medium text-muted-foreground">Margin</span>
            </div>
            <p className={`text-2xl sm:text-3xl font-bold ${profitMargin >= 50 ? 'text-green-600' : profitMargin >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
              {profitMargin.toFixed(0)}%
            </p>
            <p className="text-xs text-muted-foreground mt-1">Profit margin</p>
          </div>
        </motion.div>

        {/* Two-column layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {/* Usage by Tool */}
          <motion.div
            className="p-5 sm:p-6 bg-white border border-border rounded-2xl shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-primary" />
              Usage by Tool
            </h3>
            {usageByTool.length > 0 ? (
              <div className="space-y-3">
                {usageByTool.map(tool => {
                  const info = TOOL_LABELS[tool.tool_type];
                  const pct = totalCreditsUsed > 0 ? (tool.total_credits / totalCreditsUsed) * 100 : 0;
                  const toolCost = tool.total_credits * (info?.costPerCredit || COST_PER_CREDIT);
                  return (
                    <div key={tool.tool_type} className="space-y-1.5">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="text-muted-foreground">{info?.icon || <Sparkles className="w-4 h-4" />}</span>
                          <span className="text-sm font-medium text-foreground">{info?.label || tool.tool_type}</span>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground">
                          <span>{tool.total_uses} uses</span>
                          <span className="font-semibold text-foreground">{tool.total_credits} cr</span>
                          <span className="text-red-500">${toolCost.toFixed(3)}</span>
                        </div>
                      </div>
                      <div className="h-2 bg-muted/30 rounded-full overflow-hidden">
                        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No usage data yet</p>
            )}

            {/* Summary */}
            <div className="mt-4 pt-4 border-t border-border/30 grid grid-cols-2 gap-3">
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-muted-foreground">Active Balances</p>
                <p className="text-lg font-bold text-foreground">{totalActiveCredits.toLocaleString()}</p>
                <p className="text-[10px] text-muted-foreground">Credits in user wallets</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <p className="text-xs text-muted-foreground">Avg per Client</p>
                <p className="text-lg font-bold text-foreground">
                  {totalClients > 0 ? Math.round(totalCreditsUsed / totalClients) : 0}
                </p>
                <p className="text-[10px] text-muted-foreground">Credits used avg</p>
              </div>
            </div>
          </motion.div>

          {/* Top Users */}
          <motion.div
            className="p-5 sm:p-6 bg-white border border-border rounded-2xl shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="text-base font-semibold text-foreground mb-4 flex items-center gap-2">
              <Users className="w-4 h-4 text-primary" />
              Top AI Users
            </h3>
            {topUsers.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {topUsers.map((u, i) => {
                  const usageRatio = u.plan_allowance > 0 ? (u.total_credits / u.plan_allowance) : 0;
                  const usageLevel = usageRatio > 2 ? 'high' : usageRatio > 0.5 ? 'medium' : 'low';
                  const apiCost = u.total_credits * COST_PER_CREDIT;
                  return (
                    <div key={u.client_id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl border border-border/30">
                      <span className="text-xs font-bold text-muted-foreground w-5 text-center">#{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">{u.client_name}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                            usageLevel === 'high'
                              ? 'bg-red-100 text-red-600'
                              : usageLevel === 'medium'
                                ? 'bg-amber-100 text-amber-600'
                                : 'bg-green-100 text-green-600'
                          }`}>
                            {usageLevel === 'high' ? '🔥 HIGH' : usageLevel === 'medium' ? '📊 MED' : '✅ LOW'}
                          </span>
                        </div>
                        <p className="text-[10px] text-muted-foreground truncate">{u.client_email} · {u.client_plan}</p>
                      </div>
                      <div className="text-right flex-shrink-0 flex items-center gap-2">
                        <div>
                          <p className="text-sm font-bold text-foreground">{u.total_credits} cr</p>
                          <p className="text-[10px] text-muted-foreground">{u.total_uses} uses</p>
                          <p className="text-[10px] text-red-500">${apiCost.toFixed(2)} cost</p>
                        </div>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 flex-shrink-0"
                          title="Give 10 free credits"
                          onClick={() => giveCredits(u.client_id, u.client_name)}
                        >
                          <Gift className="w-3.5 h-3.5 text-green-600" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No usage data yet</p>
            )}
          </motion.div>
        </div>

        {/* Cost Breakdown Info */}
        <motion.div
          className="mt-6 p-5 bg-white border border-border rounded-2xl shadow-sm"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <h3 className="text-base font-semibold text-foreground mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Cost Reference (Estimated per credit)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {Object.entries(TOOL_LABELS).map(([key, info]) => (
              <div key={key} className="p-3 bg-slate-50 rounded-xl border border-border/30 text-center">
                <span className="text-muted-foreground block mb-1">{info.icon}</span>
                <p className="text-xs font-medium text-foreground">{info.label}</p>
                <p className="text-[10px] text-red-500 mt-1">${info.costPerCredit}/credit</p>
              </div>
            ))}
          </div>
          <p className="text-[10px] text-muted-foreground mt-3">
            * API costs are estimates. Revenue is real data from Stripe credit pack purchases.
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default AdminAITools;
